import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import { bai } from 'neuro-schemas';
import * as React from 'react';
import type { IMyServiceContext } from '../..';
import { useMyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import DataTable from 'Components/DataTable';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { formatPartialDate } from 'Utility/date';
import { isNumber } from 'neuro-utils';
import { omitControlProps } from 'Utility/documentHandling';
import { omit } from 'Utility/ramdaReplacement';
import browserLanguage from 'Utility/browserLanguage';
import { useAppSelector } from 'Store/index';

const {
  calculateScore,
  utils: { baiAnxietyDesc },
} = bai;

const omitControlData = (doc: { edited?: 'string' } & IBAI & Partial<IControlProps>) => {
  let data = omitControlProps(doc);
  data = omit(['edited'], data);
  return data;
};

/**
 * Formats a string with tags to include formatted text as elements
 * @param {string} str - Text string
 * @param {'b'} tag - Tag to be replaced
 * @returns {React.JSX.Element} Formatted text element
 */
const replaceTags = (str: string, tag = 'b') => {
  const regExpSplit = RegExp(`<${tag}>.+</${tag}>`);
  const normalText = str.split(regExpSplit);

  const regExpMatch = RegExp(`<${tag}>(.+)</${tag}>`);
  const matches = str.match(regExpMatch);
  matches && matches.shift();

  const returnElement = (
    <>
      {normalText.map((n, i) => (
        <React.Fragment key={n}>
          {n}
          {matches?.[i] && <span style={tag === 'b' ? { fontWeight: 600 } : {}}>{matches[i]}</span>}
        </React.Fragment>
      ))}
    </>
  );
  return returnElement;
};

const BAIScore = ({ doc, fm }: { doc: IBAI; fm: IMyServiceContext<'bai'>['fm'] }): React.JSX.Element => {
  const score = calculateScore(doc);
  const localization = isNumber(score) ? fm(`myService.ninmt.baiAnxietyDescs.${baiAnxietyDesc(score)}`) : null;
  return (
    <>
      <span style={{ fontWeight: 600 }}>{score || score === 0 ? score + ' ' : '-'}</span>
      <span>{localization}</span>
    </>
  );
};

export const Bai = (): React.JSX.Element => {
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const { editing, setEditingData, fm, viewing, setViewingObj, setEditingObj } = useMyServiceContext<'bai'>();

  const baiData = editing?.data ?? viewing?.data ?? {};

  const baiQuestions = bai.utils.baiSurveyGenerator.questions();
  const baiLocales = bai.getLocalizations(locale);

  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const field = Object.keys(values)[0];
    if (!field) return;
    const value = Object.values(values)[0];
    setEditingData?.({
      ...baiData,
      [field]: typeof value === 'string' ? parseInt(value) : value,
    });
  };

  const isEditing = !viewing && !!editing;

  const baiQuestion = (id: string) => baiQuestions.find(bdiq => bdiq.id === id);

  const baiOption = (id: string) => baiQuestions.find(bdiq => bdiq.id === id)?.options?.[(baiData as any)[id]];

  const questionLocalized = (qid: string) => {
    const localeString = baiOption(qid)?.optionLocale;
    return localeString && baiLocales[localeString];
  };

  if (viewing) {
    return (
      <>
        <BlockWrapper
          title={formatPartialDate(baiData.date)}
          buttons={[
            {
              title: 'general.edit',
              onClick: () => {
                setViewingObj(null);
                setEditingObj({ type: 'bai', data: baiData });
              },
            },
          ]}
        >
          <div style={{ width: '80%', marginBottom: '2rem' }}>
            <DataTable
              headers={['question', 'answer', 'score']}
              headersFormatter={(s: string) => fm('myService.' + s)}
              data={{
                rowData: Object.keys(omitControlData(baiData))
                  .filter(q => q !== 'date')
                  .map(q => [
                    baiLocales[baiQuestion(q)?.questionLocale ?? ''] ?? '-',
                    <span key={q + 'long'} style={{ fontWeight: 600, color: colors.black }}>{`${questionLocalized(
                      q,
                    )}`}</span>,
                    <span key={q + 'short'} style={{ fontWeight: 600, color: colors.black }}>{`${
                      baiOption(q)?.value
                    }`}</span>,
                  ]),
              }}
            />
          </div>
          <FormRow
            title={'myService.ninmt.score'}
            description={fm('myService.ninmt.baiTotalScoreDescription', { br: '\n' })}
            headerWidth={4.45}
          >
            <BAIScore doc={baiData} fm={fm} />
          </FormRow>
        </BlockWrapper>
      </>
    );
  }

  return (
    <>
      <div style={{ fontStyle: 'italic', marginBottom: '2rem', color: colors.tertiaryText }}>
        <span style={{ fontWeight: 600 }}>{`${baiLocales[
          baiQuestions[0]?.questionLocale ?? '-'
        ]?.toUpperCase()}: `}</span>
        <span>{replaceTags(baiLocales[baiQuestions[0]?.infoTextLocale ?? '-'] ?? '')}</span>
      </div>
      <FormSection>
        <>
          <FormRow title={'general.date'}>
            <InputHandler
              name="date"
              type="PartialDate"
              editing={isEditing}
              isNotCancellable={true}
              formData={{
                document: { date: baiData.date },
                onChange: onChangeSurveyForm,
              }}
              dateDefault="now"
            />
          </FormRow>
          {baiQuestions.map(q => {
            const getOptionLocale = (o: string | number) => q.options?.find(opt => opt.value === o)?.optionLocale;

            if (q.id === 'startInfo') return <React.Fragment key={q.id}></React.Fragment>;
            return (
              <FormRow key={`${q.id}`} title={baiLocales[q.questionLocale ?? '']} formatTitle={false}>
                <InputHandler
                  type="Radio"
                  name={q.id}
                  options={q.options?.map(o => o.value)}
                  editing={isEditing}
                  optionFormatter={o => baiLocales[getOptionLocale(o) ?? ''] ?? '-'}
                  formData={{
                    document: { [q.id]: baiData[q.id as keyof IBAI] },
                    onChange: onChangeSurveyForm,
                  }}
                />
              </FormRow>
            );
          })}
          <FormRow
            title={'myService.ninmt.score'}
            description={fm('myService.ninmt.baiTotalScoreDescription', { br: '\n' })}
          >
            <BAIScore doc={baiData} fm={fm} />
          </FormRow>
        </>
      </FormSection>
    </>
  );
};
